import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';

import { navigateParentToDtc, openPreconditionsDialog } from '@/features/app/MessageHandler';
import TimestampAndMileage from '@/features/engineoil/components/TimestampAndMileage';
import { ENGINE_OIL_STATE, SOURCE_DEVICE } from '@/features/engineoil/utils/constants';
import { getBarColorClass, getThresholdClass, isEngineOilOutdated } from '@/features/engineoil/utils/utils';
import type { EngineOil } from '@/generated/GeneratedApiTypes';
import useHoverIntent from '@/hooks/useHoverIntent';
import AnalyticsEvents from '@/utils/analytics/AnalyticsEvents';
import BasePanel from './BasePanel';

interface Cm4SsSeriesCardProps {
    engineOil: EngineOil;
}

// Cm4SsSeries = CM4 Sensor Signal Series
const Cm4SsSeriesCard = ({ engineOil }: Cm4SsSeriesCardProps) => {
    const intl = useIntl();
    const { asset_id, state, status, measurement, measured_at, active_dtc } = engineOil;
    const [hoverRef] = useHoverIntent(1000, () => AnalyticsEvents.hoverSensorSignalCardOlder24h(SOURCE_DEVICE.CM4));

    const isReadingNotPossible = isEngineOilOutdated(measured_at.date);
    const stateKey = isReadingNotPossible ? 'reading_not_possible' : state;
    const hasRecommendation = state !== ENGINE_OIL_STATE.OK || isReadingNotPossible;
    const hasActiveDtc = active_dtc != null;

    const thresholdClass = getThresholdClass(status, isReadingNotPossible);
    const barColorClass = getBarColorClass(status, isReadingNotPossible);
    const textColorClass = isReadingNotPossible ? 'text-color-gray' : 'text-color-gray-darkest';
    const percentage = measurement != null ? Math.round(measurement) : 0;

    return (
        <BasePanel thresholdClass={thresholdClass} testId="card-workshop-cm4-sensor_signal_series">
            <div className="display-flex flex-wrap">
                {isReadingNotPossible && (
                    <div className="text-size-14 text-color-dark">
                        <FormattedMessage id="intl-msg:engineOil.latestValidReading" />
                        &nbsp;
                    </div>
                )}
                <TimestampAndMileage timestamp={measured_at.date} mileage={measured_at.mileage_km} />
            </div>

            <div className="width-100pct display-flex align-items-center padding-top-10">
                <div className="progress margin-0 flex-1-0" style={{ height: '15px' }}>
                    <div className={`progress-bar ${barColorClass}`.trim()} style={{ width: `${percentage}%` }} />
                </div>
                <div className={`margin-left-10 ${textColorClass}`}>{`${percentage} %`}</div>
                {isReadingNotPossible && (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                <FormattedMessage id="intl-msg:engineOil.olderThan24h" />
                            </Tooltip>
                        }
                    >
                        <span
                            ref={hoverRef}
                            className={
                                'rioglyph rioglyph-exclamation-sign text-color-dark ' +
                                'text-size-16 width-auto margin-top-0 margin-left-5'
                            }
                        />
                    </OverlayTrigger>
                )}
            </div>

            <div className="text-size-14">
                <FormattedMessage id={`intl-msg:engineOil.state.${stateKey}`} />
            </div>

            {hasRecommendation && (
                <div className="text-size-12 text-color-dark">
                    <FormattedMessage id={`intl-msg:engineOil.card.cm4SsSeries.recommendation.${stateKey}`} />
                </div>
            )}

            <div className="btn-toolbar margin-top-15 display-flex" style={{ justifyContent: 'space-between' }}>
                <Button
                    iconName="rioglyph-detail-view-info"
                    bsSize={Button.SM}
                    onClick={() => {
                        AnalyticsEvents.openSensorSignalPreconditionsDialog(SOURCE_DEVICE.CM4);
                        openPreconditionsDialog(
                            asset_id,
                            intl.formatMessage({ id: 'intl-msg:engineOil.preconditions.dialog.title' }),
                        );
                    }}
                >
                    <FormattedMessage id="intl-msg:engineOil.preconditionsButton" />
                </Button>
            </div>

            {hasActiveDtc && (
                <div
                    className={
                        'margin-top-15 margin-bottom-0 display-flex ' +
                        'justify-content-between align-items-center text-size-12'
                    }
                >
                    <div className="text-color-danger display-flex align-items-center">
                        <span className="rioglyph rioglyph-error-sign margin-right-2" />
                        <FormattedMessage id={`intl-msg:engineOil.card.workshop.dtc.${active_dtc.type}`} />
                    </div>
                    <Button
                        bsStyle={Button.DANGER}
                        variant={Button.VARIANT_OUTLINE}
                        bsSize={Button.XS}
                        style={{ borderRadius: '5px' }}
                        onClick={() => {
                            AnalyticsEvents.clickSpnBtn(SOURCE_DEVICE.CM4);
                            navigateParentToDtc(active_dtc.spn);
                        }}
                    >
                        <FormattedMessage id="intl-msg:engineOil.dtc.spn" />
                        &nbsp;
                        {active_dtc.spn}
                    </Button>
                </div>
            )}
        </BasePanel>
    );
};

export default Cm4SsSeriesCard;
